import type { Message } from '../types';

/**
 * Whether a message asks for the plan whose review timed out to go on: the
 * whole of it is a go-ahead, with nothing else said.
 *
 * It was `/^(继续|继续执行|continue|resume|执行|run|go)\b/i`. `\b` is the
 * boundary between a word character and one that is not, and no Chinese
 * character is a word character: after 继续 there is none, so 「继续」 and
 * 「继续执行」 never matched, and typing them did not resume the plan. The
 * English words matched any message that began with one: 「run a compile
 * check」 and 「go back to the first version」 were sent with the timed-out
 * plan attached as approved.
 */
const RESUME_PLAN = new RegExp(
  '^(?:请)?(?:'
  + '继续(?:执行)?(?:计划|方案)?'
  + '|接着(?:做|来|执行)?'
  + '|执行(?:计划|方案)?'
  + '|(?:continue|resume|proceed|execute)(?:\\s+(?:with\\s+)?(?:the\\s+)?plan)?'
  + '|go(?:\\s+(?:on|ahead))?'
  + '|run(?:\\s+(?:it|the\\s+plan))?'
  + ')(?:吧|一下)?[\\s。.!！~]*$',
  'i',
);

export function asksToResumePlan(prompt: string): boolean {
  return RESUME_PLAN.test(prompt.trim());
}

/** What 「继续执行」 sends for a timed-out plan that has no summary (AppSecure's handleResumePlan). */
export const RESUME_PLAN_FALLBACK_PROMPT = '继续执行计划';

/**
 * Whether a user message is the one 「继续执行」 sent for a plan whose review
 * timed out: the plan's summary, sent with the plan, and not the user's words.
 * On 2026-10-20 the conversation drew it as theirs. Said by the flag the page
 * set as it sent it; on a page loaded from the server, by the turn after it,
 * which carried out a plan approved before (`planReview.resumedApproval`) and
 * whose summary the message is. A go-ahead the user typed resumes the plan too
 * (asksToResumePlan), and is theirs.
 */
function sentByResumeButton(message: Message, next: Message | undefined): boolean {
  if (message.role !== 'user') return false;
  if (message.resumesApprovedPlan) return true;
  const plan = next?.role === 'assistant' ? next.planReview : undefined;
  if (!plan?.resumedApproval) return false;
  const sent = message.content.trim();
  return sent === (plan.requirementSummary || RESUME_PLAN_FALLBACK_PROMPT).trim();
}

/** The conversation's messages, each one 「继续执行」 sent marked as such, for the conversation to draw. */
export function markResumedPlanMessages(messages: Message[]): Message[] {
  return messages.map((message, index) => (
    !message.resumesApprovedPlan && sentByResumeButton(message, messages[index + 1])
      ? { ...message, resumesApprovedPlan: true }
      : message
  ));
}
